// Libraries
import UserAvatar from "@/components/Avatar/UserAvatar";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import moment from "moment-timezone";
import { Editor } from "@tinymce/tinymce-react";
import { useEffect, useState } from "react";
import { SpinnerCircular } from "spinners-react";
import Button from "../../ui/Button/Button";
import { toast } from "react-toastify";
import { capitalize } from "@/helpers/capitalize";
import calculatePlayerLevel from "@/helpers/calculatePlayerLevel";
import reformatUrls from "@/helpers/reformatUrls";
import { transformHtml } from "@/helpers/transformHtml";
import {
    canMarkBestAnswer,
    canRemoveBestAnswer,
    getDiscussionLocation,
} from "@/helpers/bestAnswer";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useLocale, useTranslations } from "next-intl";

export default function Message(props) {
    const locale = useLocale();
    const t = useTranslations();

    let author, type;
    let session = props.session;

    if (props.message.author) {
        author = props.message.author;
        type = "original";
    } else {
        author = props.message.user;
        type = "reply";
    }

    // Calculate author level if XP is available
    const authorLevel = author?.xp !== undefined
        ? calculatePlayerLevel(author.xp).level
        : null;

    // Droits sur ce message. La session next-auth range tout sous `user` :
    // les comparaisons se faisaient sur session.userId, toujours undefined,
    // et privaient un membre du menu sur son propre message.
    const currentUserId = session?.user?.userId;
    const currentRoles = session?.user?.roles;
    const isSuperAdmin = Boolean(currentRoles?.includes("ROLE_SUPER_ADMIN"));
    const canEditMessage = author?.id === currentUserId || isSuperAdmin;
    const canMarkBest =
        type === "reply" &&
        canMarkBestAnswer({
            discussion: props.discussion,
            answer: props.message,
            userId: currentUserId,
            roles: currentRoles,
            answers: props.discussion?.messages,
        });
    const canRemoveBest =
        type === "reply" &&
        canRemoveBestAnswer({
            discussion: props.discussion,
            answer: props.message,
            roles: currentRoles,
        });
    // /interface/post/guide n'est ouverte qu'aux administrateurs côté back.
    const canMarkGuide = type === "original" && isSuperAdmin;
    const hasMenu =
        canEditMessage || canMarkBest || canRemoveBest || canMarkGuide;

    // States
    const [isClicked, setIsClicked] = useState(false);
    const [bestMessageIsUpdating, setBestMessageIsUpdating] = useState(false);
    const [message, setMessage] = useState(props.message);
    const [content, setContent] = useState(props.message.content);
    const [editing, setEditing] = useState(false);
    const [discussionIsEditing, setDiscussionIsEditing] = useState(false);
    const [editorKey, setEditorKey] = useState(1);
    const [transformedContent, setTransformedContent] = useState("");

    // Methods
    const editButtonClickedHandler = () => {
        props.setDiscussionIsEditing(true);
        setEditing(true);
        setIsClicked(false);
    };
    const editMessageButtonClickedHandler = () => {
        setContent(message.content);
        setEditing(true);
        setIsClicked(false);
    };
    const backEditClickedHandler = () => {
        if (props.title) {
            props.setTitle(props.title);
            props.setDiscussionIsEditing(false);
        }
        setContent(message.content);
        setEditing(false);
    };
    const bestMessage = async (remove = false) => {
        if (!bestMessageIsUpdating) {
            setBestMessageIsUpdating(true);

            const response = await fetch("/api/interface/post/best-answer", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    messageId: message.id,
                    ...getDiscussionLocation(props.discussion),
                    remove,
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setBestMessageIsUpdating(false);
                toast.error(fetchedData.message);
            } else {
                // Update message for discussion
                setMessage(
                    fetchedData.discussion.messages.filter(
                        (m) => m.id === message.id,
                    )[0],
                );
                props.setDiscussion(fetchedData.discussion);
                setBestMessageIsUpdating(false);
                setIsClicked(false);
                toast.success(
                    remove
                        ? t("Message_bestAnswerRemoved")
                        : t("Message_bestAnswerAdded"),
                );
            }
        }
    };
    const editDiscussionHandler = async () => {
        if (!discussionIsEditing) {
            setDiscussionIsEditing(true);

            if (type == "reply") {
                const response = await fetch("/api/interface/put/message", {
                    method: "post",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        messageId: message.id,
                        content,
                    }),
                });

                const fetchedData = await response.json();

                if (!response.ok) {
                    setDiscussionIsEditing(false);
                    toast.error(fetchedData.message);
                } else {
                    transformContent(content);
                    setDiscussionIsEditing(false);
                    setEditing(false);
                    toast.success(t("Message_messageUpdatedSuccessfully"));
                }
            } else {
                const response = await fetch("/api/interface/put/discussion", {
                    method: "post",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        discussionId: message.id,
                        content,
                        title: props.title,
                    }),
                });

                const fetchedData = await response.json();

                if (!response.ok) {
                    setDiscussionIsEditing(false);
                    toast.error(fetchedData.message);
                } else {
                    transformContent(content);
                    props.setTitle(fetchedData.discussion.title);
                    props.setDiscussionIsEditing(false);
                    setDiscussionIsEditing(false);
                    setEditing(false);
                    toast.success(t("Message_discussionUpdatedSuccessfully"));
                }
            }
        }
    };
    const transformContent = async (content) => {
        const { content: transformedContent } = await transformHtml(
            content,
            true,
        );
        setTransformedContent(transformedContent);
    };

    useEffect(() => {
        if (props.message) {
            transformContent(props.message.content);
        }
    }, [props]);

    // Helper: Render dropdown menu
    const renderDropdownMenu = () => (
        <>
            {canEditMessage && (
                <>
                    <div
                        className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 dark:text-grayDark-200 hover:bg-gray-100/80 dark:hover:bg-white/10 transition-colors cursor-pointer"
                        onClick={editMessageButtonClickedHandler}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-gray-500 dark:text-grayDark-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                        </svg>
                        {t("Message_edit")}
                    </div>
                    <div
                        className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
                        onClick={props.deleteMessage}
                    >
                        {props.discussionIsDeleting ? (
                            <SpinnerCircular thickness={90} size={20} color="#ef4444" />
                        ) : (
                            <>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                                {t("Message_delete")}
                            </>
                        )}
                    </div>
                </>
            )}
            {canMarkBest && (
                <div
                    className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 dark:text-grayDark-200 hover:bg-gray-100/80 dark:hover:bg-white/10 transition-colors cursor-pointer"
                    onClick={() => bestMessage(false)}
                >
                    {bestMessageIsUpdating ? (
                        <SpinnerCircular thickness={90} size={20} color="#10b981" />
                    ) : (
                        <>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 text-emerald-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            {t("Message_bestMessage")}
                        </>
                    )}
                </div>
            )}
            {canRemoveBest && (
                <div
                    className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 dark:text-grayDark-200 hover:bg-gray-100/80 dark:hover:bg-white/10 transition-colors cursor-pointer"
                    onClick={() => bestMessage(true)}
                >
                    {bestMessageIsUpdating ? (
                        <SpinnerCircular thickness={90} size={20} color="#10b981" />
                    ) : (
                        <>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 text-red-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            {t("Message_removeBestMessage")}
                        </>
                    )}
                </div>
            )}
            {canMarkGuide && (
                <div
                    className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 dark:text-grayDark-200 hover:bg-gray-100/80 dark:hover:bg-white/10 transition-colors cursor-pointer"
                    onClick={props.markGuide}
                >
                    {bestMessageIsUpdating ? (
                        <SpinnerCircular thickness={90} size={20} color="#f59e0b" />
                    ) : (
                        <>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 text-amber-500"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" />
                            </svg>
                            {!message.isForHelp ? t("Message_markAsGuideButton") : t("Message_unmarkAsGuideButton")}
                        </>
                    )}
                </div>
            )}
        </>
    );

    // Helper: Render message content (editor or display)
    const renderMessageContent = () => (
        <>
            {editing && session ? (
                <div className="dynamic-content">
                    <Editor
                        key={editorKey}
                        tinymceScriptSrc="/tinymce/tinymce.min.js"
                        licenseKey="gpl"
                        init={{
                            relative_urls: false,
                            remove_script_host: false,
                            height: 150,
                            max_height: 300,
                            menubar: false,
                            inline: true,
                            toolbar_location: "bottom",
                            fixed_toolbar_container: "#editor-toolbar",
                            codesample_languages: process.env.TINYMCE_CODESAMPLE_LANGUAGES,
                            codesample_global_prismjs: true,
                            plugins: "codesample anchor autolink link autoresize autosave fullscreen image lists preview searchreplace table visualblocks wordcount",
                            toolbar: "undo redo | bold italic forecolor | numlist bullist | image codesample | link anchor | restoredraft",
                            content_style: session?.user?.darkMode
                                ? ".mce-content-body {outline: none; margin: 0; max-height: 300px; overflow: auto; color: white; border: 1px solid #C4A461; border-radius: 15px; padding: 10px} .mce-content-body p {margin: 0} .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { color: #9F9FA0; padding-left: 10px } .tox-toolbar__primary { background: transparent!important} .tox-editor-header { border: 0!important } .tox-toolbar-overlord { background-color: transparent!important } .tox-toolbar__group:not(:last-of-type) { border-right: 1px solid #9F9FA0!important }"
                                : ".mce-content-body {outline: none; margin: 0; max-height: 300px; overflow: auto; border: 1px solid #C4A461; border-radius: 15px; padding: 10px} .mce-content-body p {margin: 0} .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { padding-left: 10px }  .tox-toolbar__primary { background: transparent!important} .tox-editor-header { border: 0!important } .tox-toolbar-overlord { background-color: transparent!important } .tox-toolbar__group:not(:last-of-type) { border-right: 1px solid #9F9FA0!important }",
                            placeholder: t("Message_writeYourMessage"),
                            skin: session?.user?.darkMode ? "oxide-dark" : "oxide",
                            content_css: session?.user?.darkMode
                                ? false
                                : "default",
                            image_dimensions: false,
                                                    }}
                        onChange={(e) => {
                            setContent(e.target.getContent());
                        }}
                        initialValue={message.content}
                    />
                    <div className="editor-toolbar" id="editor-toolbar"></div>
                    <div className="flex justify-between mt-5">
                        <Button type="light-purple" onClick={backEditClickedHandler}>
                            {t("Message_cancel")}
                        </Button>
                        <Button onClick={editDiscussionHandler} isLoading={discussionIsEditing} progress>
                            {t("Message_save")}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="dynamic-content leading-relaxed text-gray-700 dark:text-grayDark-300 text-[15px]">
                    {transformedContent && transformedContent != "" ? (
                        transformedContent
                    ) : (
                        <Skeleton
                            count={Math.floor(Math.random() * (5.5 - 1.5)) + 1.5}
                            width={"100%"}
                            height={19}
                            baseColor="rgba(0, 0, 0, 0.3)"
                            highlightColor="rgba(0, 0, 0, 0.15)"
                        />
                    )}
                </div>
            )}
        </>
    );

    // Helper: Render menu button
    const renderMenuButton = (isMobile = false) => (
        session && hasMenu && (
            <button
                className={`p-1.5 rounded-lg transition-all duration-200 flex-shrink-0 cursor-pointer ${
                    isMobile
                        ? "hover:bg-gray-100 dark:hover:bg-white/10"
                        : "opacity-0 group-hover/message:opacity-100 hover:bg-gray-100 dark:hover:bg-white/10"
                }`}
                onClick={() => setIsClicked(!isClicked)}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-4 h-4 text-gray-400 dark:text-grayDark-500"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
                    />
                </svg>
            </button>
        )
    );

    // Helper: Render dropdown
    const renderDropdown = (topClass = "top-8") => (
        isClicked && session && (
            <div className={`absolute right-0 ${topClass} rounded-xl overflow-hidden shadow-xl ring-1 ring-black/5 dark:ring-white/10 z-[100] backdrop-blur-xl bg-white/95 dark:bg-grayDark-800/95 min-w-[180px]`}>
                {renderDropdownMenu()}
            </div>
        )
    );

    return (
        <div
            className={`group/message relative p-4 sm:p-6 transition-all duration-200 ${
                message.bestMessage
                    ? "bg-gradient-to-br from-emerald-50/80 via-green-50/40 to-teal-50/30 dark:from-emerald-950/30 dark:via-green-950/15 dark:to-teal-950/10"
                    : "hover:bg-gray-50/60 dark:hover:bg-white/[0.02]"
            }`}
        >
            {/* Best message indicator - left bar */}
            {message.bestMessage && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-400 to-green-500 rounded-r-full" />
            )}

            {/* ===== DESKTOP LAYOUT ===== */}
            <div className="hidden sm:flex gap-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                    <Link href={`/@${author.pseudo ? author.pseudo : `believer-${author.id}`}`}>
                        <div className="relative w-[50px] h-[50px]">
                            <div className="w-[50px] h-[50px] rounded-full overflow-hidden shadow-sm ring-1 ring-black/5 dark:ring-white/10 hover:ring-primary/50 transition-all">
                                <UserAvatar user={author} alt="Image" className="object-cover rounded-full" fill />
                            </div>
                            {authorLevel && (
                                <div
                                    className="absolute -bottom-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-gray-900 dark:bg-white text-white dark:text-grayDark-900 text-[10px] font-semibold flex items-center justify-center"
                                    title={t("community_level", { level: authorLevel })}
                                >
                                    {authorLevel}
                                </div>
                            )}
                        </div>
                    </Link>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center justify-between gap-3 mb-3 relative">
                        <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                            <Link
                                href={`/@${author.pseudo ? author.pseudo : `believer-${author.id}`}`}
                                className="font-semibold text-[15px] text-gray-900 dark:text-white hover:text-primary dark:hover:text-primary-100 transition-colors"
                            >
                                {author.showPseudo && author.pseudo
                                    ? capitalize(author.pseudo)
                                    : `${capitalize(author.firstName)} ${capitalize(author.lastName)}`}
                            </Link>
                            <span className="text-gray-300 dark:text-grayDark-600">·</span>
                            <time className="text-xs text-gray-400 dark:text-grayDark-500">
                                {moment
                                    .utc(message.creationDate)
                                    .tz(session ? session.user.timezone : "Europe/Paris")
                                    .locale(locale)
                                    .fromNow()}
                            </time>
                            {message.bestMessage && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t("Message_bestAnswerBadge")}
                                </span>
                            )}
                        </div>
                        <div className="relative">
                            {renderMenuButton(false)}
                            {renderDropdown("top-8")}
                        </div>
                    </div>

                    {/* Message content */}
                    {renderMessageContent()}
                </div>
            </div>

            {/* ===== MOBILE LAYOUT ===== */}
            <div className="sm:hidden">
                {/* Row 1: Avatar + Name + Date + Menu */}
                <div className="flex items-center gap-3 relative">
                    <Link href={`/@${author.pseudo ? author.pseudo : `believer-${author.id}`}`} className="flex-shrink-0">
                        <div className="relative w-10 h-10">
                            <div className="w-10 h-10 rounded-full overflow-hidden shadow-sm ring-1 ring-black/5 dark:ring-white/10">
                                <UserAvatar user={author} alt="Image" className="object-cover rounded-full" fill />
                            </div>
                            {authorLevel && (
                                <div
                                    className="absolute -bottom-0.5 -right-0.5 min-w-4 h-4 px-0.5 rounded-full bg-gray-900 dark:bg-white text-white dark:text-grayDark-900 text-[9px] font-semibold flex items-center justify-center"
                                    title={t("community_level", { level: authorLevel })}
                                >
                                    {authorLevel}
                                </div>
                            )}
                        </div>
                    </Link>
                    <div className="flex-1 min-w-0 flex items-center flex-wrap gap-x-2 gap-y-0.5">
                        <Link
                            href={`/@${author.pseudo ? author.pseudo : `believer-${author.id}`}`}
                            className="font-semibold text-sm text-gray-900 dark:text-white hover:text-primary dark:hover:text-primary-100 transition-colors"
                        >
                            {author.showPseudo && author.pseudo
                                ? capitalize(author.pseudo)
                                : `${capitalize(author.firstName)} ${capitalize(author.lastName)}`}
                        </Link>
                        <time className="text-[11px] text-gray-400 dark:text-grayDark-500 flex-shrink-0">
                            {moment
                                .utc(message.creationDate)
                                .tz(session ? session.user.timezone : "Europe/Paris")
                                .locale(locale)
                                .fromNow()}
                        </time>
                    </div>
                    <div className="relative">
                        {renderMenuButton(true)}
                        {renderDropdown("top-10")}
                    </div>
                </div>

                {/* Row 2: Content - full width */}
                <div className="mt-3">
                    {renderMessageContent()}
                </div>

                {/* Row 3: Best answer badge at the bottom */}
                {message.bestMessage && (
                    <div className="mt-4">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            {t("Message_bestAnswerBadge")}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}
